import commentsModel from "../models/comments.model";
import httpService from "@/services/http.service";
import { ref } from "vue";
import {
  collection,
  getDocs,
  getFirestore,
  limit,
  onSnapshot,
  orderBy,
  query,
  startAfter,
  where,
  type QueryDocumentSnapshot,
} from "firebase/firestore";
import { usePosts } from "./posts.composables";
import { CommentCountModel } from "../models/commentcount.model";
import { useUserInterface } from "./userinterface.composable";
import utilService from "@/services/util.service";
import { TYPE } from "vue-toastification";
import { useReactionStore } from '@/services/reaction.store'; // Import the reaction store

const COMMENTS_PAGE_SIZE = 5;

let unSubscribeNewComments: any;
const comments = ref([] as any[]);
const toggleReply = ref<boolean>(false);
const hasMoreComments = ref(false);
const isLoadingMoreComments = ref(false);
const lastCommentDoc = ref<QueryDocumentSnapshot | null>(null);
const currentPostId = ref("");

export const useComment = () => {
  const isLoading = ref<boolean>(false);
  const { checkIfTagExist, getTextarea } = useUserInterface();
  const comment = ref(new commentsModel());
    const reactionStore = useReactionStore(); // Use the reaction store

  const { commentCount } = usePosts();
  const brText = "<br><br>";
  const showDeleteLoading = ref(false);

  const isProcessing = ref(false);
  let forumReplies = ref<any[]>([]);

  const getDb = () => {
    const db = getFirestore();
    return db;
  };

  const addComment = async (
    ev: any,
    payload: any,
    sourceId: string,
    sourceType: string,
    postId: string,
    onSend: boolean = false
  ) => {
    try {
      // enter event
      // if (isProcessing.value) return;
      if (ev.keyCode == 13 || onSend) {
        if (ev.shiftKey) {
          if (!ev.target.innerHTML.length) {
            ev.preventDefault();
          }
        } else {
          ev.preventDefault();
          comment.value.sourceId = sourceId;
          comment.value.postId = postId;
          comment.value.sourceType = sourceType;
          getTextarea(
            "getValue",
            null,
            comment.value,
            "commentTextField" + sourceId
          );
          comment.value.suggestions = {};
          if (comment.value.stockTags.length) {
            checkIfTagExist("$", comment.value.body!, comment.value.stockTags);
          }
          if (comment.value.userTags.length) {
            checkIfTagExist(
              "@",
              comment.value.body!,
              comment.value.userTags,
              "userName"
            );
          }
          if (
            (comment.value.body && comment.value.body.length) ||
            (comment.value.media && comment.value.media.length)
          ) {
            if (comment.value.body && comment.value.body.length > 8000) {
              utilService.showToast(
                "Comments length should not be more than 8000 Characters",
                TYPE.ERROR
              );
            }
            isProcessing.value = true; // Mark as processing
            isLoading.value = true;
            const response = await httpService.addComment(payload);
            if (response) {
              // utilService.showToast(comment.value.sourceType === "post" ? "Comment Added Successfully." : "Replay Added Successfully.");
              getTextarea(
                "clear",
                null,
                comment.value,
                "commentTextField" + sourceId
              );
              comment.value.media = [];
              if (comment.value.sourceType === "post") {
                updateCommentCount("add", sourceId, sourceType);
              }
              isLoading.value = false;
              isProcessing.value = false; // Mark as processing
              return true;
            }
            isLoading.value = false;
            return false;
          }
        }
      }
    } catch (e) {
      isLoading.value = false;
      isProcessing.value = false; // Mark as processing
    }
  };

  const deleteComment = async (id: string, allComments: any, isComment:boolean = true) => {
    try {
      const confirmRes = await utilService.showConfirmationDialog(
        "Are you sure you want to delete comment?"
      );
      if (confirmRes) {
        showDeleteLoading.value = true;
        const response = await httpService.removeComment(id);
        if(!isComment && response.data.isSuccess){
          const index = allComments.findIndex((com:any) => com._id == id)
          allComments.splice(index, 1)
          
        }
      }
    } catch (error) {
    } finally {
      showDeleteLoading.value = false;
    }
  };

  const updateCommentCount = (
    type: string,
    sourceId: string,
    sourceType: string
  ) => {
    if (type === "add") {
      let index = commentCount.value.findIndex(
        (x) => x.sourceId === sourceId && x.sourceType === sourceType
      );
      if (index !== -1) {
        // commentCount.value[index].commentCount =
        //   commentCount.value[index].commentCount! + 1;
      } else {
        const tempCommentCountModel = new CommentCountModel();
        tempCommentCountModel.commentCount = 1;
        tempCommentCountModel.sourceId = sourceId;
        tempCommentCountModel.sourceType = sourceType;
        commentCount.value.push(tempCommentCountModel);
      }
    }
  };

  const normalizeCommentData = (data: Record<string, any>) => ({
    ...data,
    _id: data._id ?? data.id,
  });

  const getCommentSortTime = (comment: any) =>
    comment.createdOn ?? Number.parseInt(comment._id, 10) ?? 0;

  const sortTopLevelComments = () => {
    comments.value.sort(
      (a, b) => getCommentSortTime(a) - getCommentSortTime(b)
    );
  };

  const buildTopLevelCommentsQuery = (
    storage: ReturnType<typeof getFirestore>,
    postId: string,
    pageSize: number,
    cursor: QueryDocumentSnapshot | null = null
  ) => {
    const constraints: Parameters<typeof query>[1][] = [
      where("postId", "==", postId),
      where("sourceType", "in", ["post", "papers"]),
      orderBy("createdOn", "asc"),
      limit(pageSize),
    ];
    if (cursor) {
      constraints.push(startAfter(cursor));
    }
    return query(collection(storage, "comments"), ...constraints);
  };

  const fetchRepliesForComments = async (
    postId: string,
    parentIds: string[]
  ) => {
    if (!parentIds.length) return;

    const storage = getFirestore();
    const q = query(
      collection(storage, "comments"),
      where("postId", "==", postId),
      where("sourceType", "==", "comment"),
      where("sourceId", "in", parentIds.slice(0, 30))
    );
    const snapshot = await getDocs(q);
    snapshot.docs.forEach((doc) => {
      addReplies(normalizeCommentData(doc.data()), "added");
    });
  };

  const setupNewCommentsListener = (postId: string) => {
    if (unSubscribeNewComments) {
      unSubscribeNewComments();
      unSubscribeNewComments = null;
    }

    const storage = getFirestore();
    const q = query(
      collection(storage, "comments"),
      where("postId", "==", postId),
      where("sourceType", "in", ["post", "papers"]),
      orderBy("createdOn", "desc"),
      limit(5)
    );

    unSubscribeNewComments = onSnapshot(q, (querySnapshot) => {
      querySnapshot.docChanges().forEach((change) => {
        const commentData = normalizeCommentData(change.doc.data());
        if (change.type === "removed") {
          addComments(commentData, "removed");
          return;
        }
        const exists = comments.value.some((c) => c._id === commentData._id);
        if (!exists) {
          addComments(commentData, "added");
          sortTopLevelComments();
        } else if (change.type === "modified") {
          addComments(commentData, "modified");
        }
      });
    });
  };

  const loadMoreComments = async (reset = false) => {
    if (!currentPostId.value || isLoadingMoreComments.value) return;
    if (!reset && !hasMoreComments.value) return;

    isLoadingMoreComments.value = true;
    const storage = getFirestore();

    try {
      const snapshot = await getDocs(
        buildTopLevelCommentsQuery(
          storage,
          currentPostId.value,
          COMMENTS_PAGE_SIZE,
          reset ? null : lastCommentDoc.value
        )
      );

      const newParentIds: string[] = [];
      snapshot.docs.forEach((doc) => {
        const commentData = normalizeCommentData(doc.data());
        const exists = comments.value.some((c) => c._id === commentData._id);
        if (!exists) {
          addComments(commentData, "added");
          newParentIds.push(commentData._id);
        }
      });

      if (snapshot.docs.length) {
        lastCommentDoc.value = snapshot.docs[snapshot.docs.length - 1];
      }

      hasMoreComments.value = snapshot.docs.length === COMMENTS_PAGE_SIZE;
      sortTopLevelComments();
      await fetchRepliesForComments(currentPostId.value, newParentIds);
    } finally {
      isLoadingMoreComments.value = false;
    }
  };

  const getComments = async (postId: string) => {
    currentPostId.value = postId;
    comments.value = [];
    forumReplies.value = [];
    lastCommentDoc.value = null;
    hasMoreComments.value = false;

    if (unSubscribeNewComments) {
      unSubscribeNewComments();
      unSubscribeNewComments = null;
    }

    await loadMoreComments(true);
    setupNewCommentsListener(postId);
  };

  const addReplies = (commentData: any, type: string) => {
    if (type === "added" || type === "modified") {
      let index = comments.value?.findIndex(
        (x) => x._id === commentData.sourceId
      );
      if (index === -1) {
        comments.value?.push({
          _id: commentData.sourceId,
          replies: [commentData],
        });
      } else {
        if (!comments.value![index!].replies) {
          comments.value![index!].replies = [];
        }
        let repliesIndex = comments.value![index!].replies.findIndex(
          (rep: any) => rep._id === commentData._id
        );
        if (repliesIndex !== -1) {
          comments.value![index!].replies[repliesIndex] = { ...commentData };
        } else {
          comments.value![index!].replies.push({ ...commentData });
        }
      }
    }
  };

  const addComments = (commentData: any, type: string) => {
    if (type === "added") {
      let index = comments.value?.findIndex((x) => x._id === commentData._id);
      if (index !== -1) {
        Object.assign(comments.value![index!], commentData);
      } else {
        comments.value?.push(commentData);
      }
    } else if (type === "modified") {
      let index = comments.value?.findIndex((x) => x._id === commentData._id);
      if (index !== -1) {
        comments.value![index!] = { ...commentData };
      }
    } else if (type === "removed") {
      let index = comments.value?.findIndex((x) => x._id === commentData._id);
      if (index !== -1) {
        comments.value!.splice(index!, 1);
      }
    }
  };

  const clearComments = () => {
    if (unSubscribeNewComments) {
      unSubscribeNewComments();
      unSubscribeNewComments = null;
    }
    comments.value = [];
    forumReplies.value = [];
    lastCommentDoc.value = null;
    hasMoreComments.value = false;
    currentPostId.value = "";
  };

  return {
    isLoading,
    comment,
    comments,
    forumReplies,
    showDeleteLoading,
    addComment,
    getDb,
    getComments,
    loadMoreComments: () => loadMoreComments(false),
    hasMoreComments,
    isLoadingMoreComments,
    clearComments,
    deleteComment,
    toggleReply,
  };
};
